import { calcEffectiveBS } from "@/constants/game.constants"
import {
  BattleCardBreakdown,
  BattleCardView,
  BattleOutcome,
  BattlePlayerInfo,
  BattleSideReveal,
  DeckRoundResultPayload,
} from "@/types/battle"
import { mergeCardShardSlots } from "@/utils/battleCards"

function normalizeCombat(combat: BattleCardBreakdown): BattleCardBreakdown {
  return {
    ...combat,
    effectiveBS: calcEffectiveBS(combat),
  }
}

/**
 * Слот A (first*) принадлежит вам?
 * Приоритет: карта из вашей колоды / last played → firstPlayerUserId.
 * Нельзя слепо верить firstPlayerUserId === 0 (часто missing → всегда «чужая»).
 */
export function resolveFirstSlotIsYou(
  round: Pick<
    DeckRoundResultPayload,
    "firstCard" | "secondCard" | "firstPlayerUserId"
  >,
  youUserId: number,
  options?: {
    knownFirstPlayerUserId?: number | null
    yourDeck?: BattleCardView[] | null
    yourPlayedCardId?: string | null
  }
): boolean {
  const playedId = options?.yourPlayedCardId?.trim() || null
  if (playedId) {
    if (round.firstCard.id === playedId) return true
    if (round.secondCard.id === playedId) return false
  }

  const deckIds = new Set(
    (options?.yourDeck ?? [])
      .map((card) => card.id)
      .filter((id) => id.length > 0)
  )
  if (deckIds.size > 0) {
    const firstInDeck = deckIds.has(round.firstCard.id)
    const secondInDeck = deckIds.has(round.secondCard.id)
    if (firstInDeck !== secondInDeck) return firstInDeck
  }

  const firstPlayerId =
    round.firstPlayerUserId > 0
      ? round.firstPlayerUserId
      : options?.knownFirstPlayerUserId != null &&
          options.knownFirstPlayerUserId > 0
        ? options.knownFirstPlayerUserId
        : 0

  if (firstPlayerId > 0 && youUserId > 0) {
    return firstPlayerId === youUserId
  }

  // Нет якоря — не инвертируем слоты (first ≈ вы для персонализированных payload).
  return true
}

export function mapDeckRoundToRevealSides(
  round: DeckRoundResultPayload,
  you: BattlePlayerInfo,
  opponent: BattlePlayerInfo,
  youUserId: number,
  knownFirstPlayerUserId?: number | null,
  yourDeck?: BattleCardView[] | null,
  yourPlayedCardId?: string | null
): { yourSide: BattleSideReveal; opponentSide: BattleSideReveal } {
  const firstIsYou = resolveFirstSlotIsYou(round, youUserId, {
    knownFirstPlayerUserId,
    yourDeck,
    yourPlayedCardId,
  })
  const yourCardRaw = firstIsYou ? round.firstCard : round.secondCard
  const opponentCard = firstIsYou ? round.secondCard : round.firstCard
  const fromDeck = yourDeck?.find((card) => card.id === yourCardRaw.id)
  const yourCard = mergeCardShardSlots(yourCardRaw, fromDeck ?? null)
  const yourCombat = normalizeCombat(
    firstIsYou ? round.firstCombat : round.secondCombat
  )
  const opponentCombat = normalizeCombat(
    firstIsYou ? round.secondCombat : round.firstCombat
  )

  let yourOutcome: BattleOutcome = "DRAW"
  if (round.roundWinnerUserId === youUserId) yourOutcome = "WIN"
  else if (round.roundWinnerUserId != null) yourOutcome = "LOSE"

  let opponentOutcome: BattleOutcome = "DRAW"
  if (round.roundWinnerUserId === opponent.userId) opponentOutcome = "WIN"
  else if (round.roundWinnerUserId != null) opponentOutcome = "LOSE"

  return {
    yourSide: {
      player: you,
      card: yourCard,
      combat: yourCombat,
      outcome: yourOutcome,
      ptsDelta: 0,
      coinsDelta: 0,
      rewardsApplied: false,
    },
    opponentSide: {
      player: opponent,
      card: opponentCard,
      combat: opponentCombat,
      outcome: opponentOutcome,
      ptsDelta: 0,
      coinsDelta: 0,
      rewardsApplied: false,
    },
  }
}
